/**
 * Minimal, XSS-safe markdown → HTML for assistant messages.
 *
 * Safety model: escape ALL HTML entities first, so the input can never emit a
 * tag of its own (covers model output AND untrusted tool results). Only then do
 * we introduce a fixed, safe tag set — <strong>, <em>, <a>, <br>. Links are
 * restricted to http(s) and get rel="noopener noreferrer" target="_blank".
 * No dependency, no innerHTML of raw input.
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/** Build a safe anchor. `escapedUrl` is already HTML-escaped display text. */
function anchor(escapedUrl: string, label: string): string {
  // href must be a clean http(s) URL; reverse the entity escape for the URL
  // value and drop anything that could break out of the attribute.
  const href = escapedUrl.replace(/&amp;/g, '&').replace(/["'<>\s]/g, '')
  if (!/^https?:\/\//i.test(href)) return label
  return `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`
}

export function renderMarkdown(input: string): string {
  let s = escapeHtml(input)

  // [text](url)
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/gi, (_m, text, url) => anchor(url, text))
  // bare URLs not already inside an anchor href
  s = s.replace(/(^|[\s(])((https?:\/\/[^\s<)]+))/gi, (_m, pre, url) => `${pre}${anchor(url, url)}`)

  // **bold** and __bold__
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  s = s.replace(/__([^_]+)__/g, '<strong>$1</strong>')
  // *italic* / _italic_ (avoid touching the bold we just made)
  s = s.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>')
  s = s.replace(/(^|[^_])_([^_\n]+)_(?!_)/g, '$1<em>$2</em>')

  // line breaks
  s = s.replace(/\n/g, '<br>')
  return s
}
